import { DateTime } from '@ksyos/date-time';
import { uniq } from 'lodash';
import { useState } from 'react';
import { formatString } from '@common/utils/formatString';
import { joinArrayWithConjunction } from '@common/utils/textUtils';
import { getDistributions } from '@frontend/services/api/serviceOrganizationRolesApiService';
import { getRoles } from '@frontend/services/api/serviceOrganizationSettingApiService';
import { fetchJson } from '@frontend/store/fetch';
import { getLanguageCode, getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { StopRightsModal } from '../../../elements/StopRightsModal';

interface IServiceOrganizationSettingStopModalProps {
    onClose: () => void;
    organizationName: string;
    reloadServiceOrganizationSetting: () => Promise<void>;
    serviceOrganizationSettingIds: string[];
    serviceOrServiceGroupOrCarePathName: string;
    startDate: Date;
    stopCarePath?: boolean;
}

export function ServiceOrganizationSettingStopModal(props: IServiceOrganizationSettingStopModalProps) {
    const defaultAlertMessage = formatString(
        getSelectedLanguage().stopServiceOrganizationSettings.activeProcessesWarning,
        [props.serviceOrServiceGroupOrCarePathName, props.organizationName]
    );
    const abortSignal = useAbortSignal();
    const [alertMessages, setAlertMessages] = useState<string[]>([defaultAlertMessage]);
    const endDate = DateTime.now();

    return (
        <StopRightsModal
            alertMessages={alertMessages}
            bodyText={formatString(getSelectedLanguage().stopServiceOrganizationSettings.sureToStop, [
                getSelectedLanguage().stopServiceOrganizationSettings[
                    props.stopCarePath ? 'theCarePath' : 'theService'
                ],
                props.serviceOrServiceGroupOrCarePathName,
            ])}
            endDate={endDate.format('DD-MM-YYYY')}
            onClose={props.onClose}
            onStopRight={async () => await onStopRight()}
            title={formatString(getSelectedLanguage().service.stopService, [props.serviceOrServiceGroupOrCarePathName])}
        />
    );

    async function getOrganizationsWhereRolesAreDistributed(): Promise<string[]> {
        const organizationNames: string[] = [];
        for (const serviceOrganizationSettingId of props.serviceOrganizationSettingIds) {
            const { items: serviceOrganizationSettingRoles } = await getRoles(
                serviceOrganizationSettingId,
                abortSignal
            );

            for (const serviceOrganizationSettingRole of serviceOrganizationSettingRoles) {
                const { items } = await getDistributions(serviceOrganizationSettingRole.id, abortSignal);
                const names = items.map((item) => item.serviceOrganizationSetting.organization.name);
                organizationNames.push(...names);
            }
        }

        return uniq(organizationNames);
    }

    async function onStopRight(): Promise<void> {
        // Do not stop if the service organization setting has roles that are distributed
        const organizationNames = await getOrganizationsWhereRolesAreDistributed();
        if (organizationNames.length > 0) {
            const alertMessage = formatString(
                getSelectedLanguage().serviceOrganizationSettingRoleDistributions
                    .cannotDeleteServiceOrganizationSetting,
                [joinArrayWithConjunction(organizationNames, getLanguageCode())]
            );
            const nextAlertMessages = [defaultAlertMessage, alertMessage];
            setAlertMessages(nextAlertMessages);

            return;
        }

        await fetchJson({
            body: {
                endDate: endDate.format('YYYY-MM-DD'),
                serviceOrganizationSettingIds: props.serviceOrganizationSettingIds,
                startDate: props.startDate,
            },
            method: 'POST',
            signal: abortSignal,
            url: '/api/serviceOrganizationSettings/endDate',
        });
        await props.reloadServiceOrganizationSetting();
        props.onClose();
    }
}
